import { Injectable } from "@angular/core";

import { BehaviorSubject, Observable } from "rxjs";
import { ProductManagementSearchRequest } from "../../components/pbm/product-management/search-product/product-management-search-request";
import { ProductManagementSearchResult } from "../../components/pbm/product-management/search-product/product-management-search-result";
import { ProductSubtype } from "../../models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "../../models/domains/pbm/Benefit/product-type";
import { TherapeuticClass } from "../../models/domains/pbm/Benefit/therapeutic-class";
import { Pricing } from "../../models/domains/pbm/product-management/pricing";
import { PricingInfoViewItem } from "../../models/domains/pbm/product-management/pricing-info-view-item.model";
import { PriceRegime } from "../../models/domains/pbm/product-management/price-regime";
import { ProductLabelInfo } from "../../models/domains/pbm/product-management/product-label-info";
import { ProductUseValidity } from "../../models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "../../models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "../../models/domains/pbm/product-management/use-type";
import { ProductManagementRepository } from "../../repositories/pbm/product-management.repository";
import { TemplateProductPricing } from "../../models/domains/pbm/product-management/template-product-pricing";

@Injectable({ providedIn: "root" })
export class ProductManagementService {
  private selectedProduct = new BehaviorSubject<ProductLabelInfo>(null);
  private pricingProduct = new ProductLabelInfo();
  private lastRequest: ProductManagementSearchRequest;

  constructor(private productManagementRepository: ProductManagementRepository) {}

  public getProduct(): Observable<ProductLabelInfo> {
    return this.selectedProduct.asObservable();
  }

  public setProduct(value: ProductLabelInfo): void {
    this.pricingProduct = value;
    this.selectedProduct.next(value);
  }

  public getPricingProduct(): ProductLabelInfo {
    return this.pricingProduct;
  }

  public async searchProducts(request: ProductManagementSearchRequest): Promise<ProductManagementSearchResult> {
    try {
      this.lastRequest = request;
      return Promise.resolve(await this.productManagementRepository.searchProducts(request));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productPricings(productKey: string): Promise<Pricing[]> {
    try {
      return Promise.resolve(await this.productManagementRepository.productPricings(productKey));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productPrincingTemplate(): Promise<TemplateProductPricing[]> {
    try {
      return Promise.resolve(await this.productManagementRepository.productPrincingTemplate());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async insertProductPrincing(productKey: string, pricingInfoViewItems: PricingInfoViewItem[]): Promise<Pricing[]> {
    try {
      return Promise.resolve(await this.productManagementRepository.insertProductPrincing(productKey, pricingInfoViewItems));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async updateProductPricing(productKey: string, pricingInfoViewItems: PricingInfoViewItem[]): Promise<Pricing[]> {
    try {
      return Promise.resolve(await this.productManagementRepository.updateProductPricing(productKey, pricingInfoViewItems));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async deleteProductPricing(productKey: string, pricingKey: string): Promise<Pricing[]> {
    try {
      return Promise.resolve(await this.productManagementRepository.deleteProductPricing(productKey, pricingKey));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productTypes(): Promise<ProductType[]> {
    try {
      return Promise.resolve(this.productManagementRepository.productTypes());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productSubTypesByType(productType: ProductType): Promise<ProductSubtype[]> {
    try {
      return Promise.resolve(this.productManagementRepository.productSubTypes(productType));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async therapeuticClassByKey(key: string): Promise<TherapeuticClass> {
    try {
      return Promise.resolve(this.productManagementRepository.therapeuticClassByKey(key));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async productUseValidities(): Promise<ProductUseValidity[]> {
    try {
      return Promise.resolve(this.productManagementRepository.productUseValidities());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async priceRegimes(): Promise<PriceRegime[]> {
    try {
      return Promise.resolve(this.productManagementRepository.priceRegimes());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async useTypes(): Promise<UseType[]> {
    try {
      return Promise.resolve(this.productManagementRepository.useTypes());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async tributaryCreditConcessions(): Promise<TributaryCreditConcession[]> {
    try {
      return Promise.resolve(this.productManagementRepository.tributaryCreditConcessions());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async updateProduct(product: ProductLabelInfo): Promise<void> {
    try {
      return Promise.resolve(this.productManagementRepository.updateProduct(product));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async insertProduct(product: ProductLabelInfo): Promise<string> {
    try {
      return Promise.resolve(this.productManagementRepository.insertProduct(product));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public getLastRequest(): ProductManagementSearchRequest {
    return this.lastRequest;
  }
}
